export const STANDARD_KEY = "Standard";
export const OVERLAP_KEY = "Overlap (Worst Case)";

export const SCENARIO_SPECIFIC_PREFIX = "Scenario Specific";

export const WIND_ROSE_SERIES_BASE_ORDER = [
  STANDARD_KEY,
  OVERLAP_KEY,
  "Worst Case",
  "Overlap",
];

export const isScenarioSpecificSeriesKey = (key = "") =>
  String(key).toLowerCase().startsWith(SCENARIO_SPECIFIC_PREFIX.toLowerCase());

export const filterWindRoseSeriesBySensorCount = (
  seriesMap = {},
  sensorCount = 0,
) => {
  if (sensorCount > 1) return seriesMap;
  return Object.fromEntries(
    Object.entries(seriesMap || {}).filter(
      ([key]) => !isScenarioSpecificSeriesKey(key),
    ),
  );
};

const DIRECTION_KEYS_15 = Array.from({ length: 24 }, (_, i) => String(i * 15));
const DIRECTION_KEYS_15_DOT = Array.from(
  { length: 24 },
  (_, i) => `${i * 15}.0`,
);
const DIRECTION_KEYS_225 = [
  "0",
  "22.5",
  "45.0",
  "67.5",
  "90.0",
  "112.5",
  "135.0",
  "157.5",
  "180.0",
  "202.5",
  "225.0",
  "247.5",
  "270.0",
  "292.5",
  "315.0",
  "337.5",
];

export const KNOWN_DIRECTION_KEYS = new Set([
  ...DIRECTION_KEYS_15,
  ...DIRECTION_KEYS_15_DOT,
  ...DIRECTION_KEYS_225,
]);

/** Row-level fields that are never wind-rose series names */
const RESERVED_ROW_KEYS = new Set([
  "position",
  "coordinates",
  "id",
  "ID",
  "height",
  "overall score",
  "overall_score",
  "overallScore",
  "OverallScore",
  "directional data",
  "name",
  "Name",
  "sensor_id",
  "sensorId",
  "windRoseSeries",
  "primaryWindRoseKey",
]);

export const isDirectionEntry = (key, value) => {
  if (RESERVED_ROW_KEYS.has(key)) return false;
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  if (KNOWN_DIRECTION_KEYS.has(key)) return true;
  return (
    !Number.isNaN(Number(key)) &&
    (value.velocity != null || value.direction != null)
  );
};

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/** Pull direction buckets from a flat sensor row (legacy shape). */
export const extractTopLevelDirections = (row = {}) => {
  const extracted = {};
  for (const [key, value] of Object.entries(row)) {
    if (isDirectionEntry(key, value)) extracted[key] = value;
  }
  return extracted;
};

const asDirectionalObject = (value) => {
  const parsed = parseMaybeJson(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
    return null;
  return Object.keys(parsed).length > 0 ? parsed : null;
};

const looksLikeDirectionalMap = (obj) =>
  Object.entries(obj || {}).some(([key, value]) =>
    isDirectionEntry(key, value),
  );

export const extractWindRoseSeries = (row = {}) => {
  const series = {};

  for (const [key, value] of Object.entries(row)) {
    if (RESERVED_ROW_KEYS.has(key)) continue;
    if (value == null) continue;
    const directional = asDirectionalObject(value);
    if (directional && looksLikeDirectionalMap(directional)) {
      series[key] = directional;
    }
  }

  if (!Object.keys(series).length) {
    const legacy =
      asDirectionalObject(row["directional data"]) ||
      extractTopLevelDirections(row);
    if (Object.keys(legacy).length) {
      series[STANDARD_KEY] = legacy;
    }
  }

  return orderWindRoseSeries(series);
};

export const orderWindRoseSeries = (seriesMap = {}) => {
  const keys = Object.keys(seriesMap);
  if (!keys.length) return {};

  const ordered = [];
  const pushIfPresent = (key) => {
    if (seriesMap[key] && !ordered.includes(key)) ordered.push(key);
  };

  WIND_ROSE_SERIES_BASE_ORDER.forEach(pushIfPresent);
  keys.filter(isScenarioSpecificSeriesKey).forEach(pushIfPresent);
  keys.forEach(pushIfPresent);

  return Object.fromEntries(ordered.map((key) => [key, seriesMap[key]]));
};

export const resolvePrimaryWindRoseSeries = (row = {}) => {
  const series = row.windRoseSeries || extractWindRoseSeries(row);
  if (series[STANDARD_KEY]) return series[STANDARD_KEY];
  const firstKey = Object.keys(series)[0];
  return firstKey ? series[firstKey] : {};
};

export const resolveStandardDirectionalData = (row = {}) =>
  resolvePrimaryWindRoseSeries(row);

export const resolveOverlapDirectionalData = (row = {}) => {
  const series = row.windRoseSeries || extractWindRoseSeries(row);
  return (
    asDirectionalObject(series[OVERLAP_KEY]) ||
    asDirectionalObject(series["Worst Case"]) ||
    asDirectionalObject(series["Overlap"]) ||
    null
  );
};

export const isNewSensorPlacementFormat = (row) => {
  if (!row || typeof row !== "object") return false;

  const hasNamedSeries = Object.keys(row).some((key) => {
    if (RESERVED_ROW_KEYS.has(key)) return false;
    const directional = asDirectionalObject(row[key]);
    return directional && looksLikeDirectionalMap(directional);
  });
  if (hasNamedSeries) return true;

  return (
    row.position != null &&
    !Array.isArray(row.position) &&
    typeof row.position === "object"
  );
};

const toLocalArray = (local) => {
  if (!local) return [0, 0, 0];
  return [local.x ?? 0, local.y ?? 0, local.z ?? 0];
};

export const resolveSensorId = (row = {}) => {
  const raw =
    row.ID ??
    row.id ??
    row.sensor_id ??
    row.sensorId ??
    row.name ??
    row.Name ??
    "";
  if (raw === null || raw === undefined || raw === "") return "";
  const str = String(raw);
  // Legacy display stripped a 4-char prefix (e.g. "SENS5719.0" -> "5719.0")
  if (str.length > 4 && /^[A-Za-z]{4}\d/.test(str)) return str.substring(4);
  return str;
};

export const resolveOverallScore = (row = {}) => {
  const direct =
    row["overall score"] ??
    row.overall_score ??
    row.overallScore ??
    row.OverallScore;
  const n = Number(direct);
  if (Number.isFinite(n)) return n;

  const primary = resolvePrimaryWindRoseSeries(row);
  const scores = Object.values(primary || {})
    .map((entry) => Number(entry?.score))
    .filter((score) => Number.isFinite(score));
  if (!scores.length) return 0;
  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
};

/** Convert legacy sensor-placement rows into the new shape. */
export const wrapLegacySensorPlacementRow = (row) => {
  const pos = Array.isArray(row.position) ? row.position : [0, 0, 0];
  const windRoseSeries = extractWindRoseSeries(row);
  const primary =
    windRoseSeries[STANDARD_KEY] || Object.values(windRoseSeries)[0] || {};
  const overallScore = resolveOverallScore(row);
  const id = resolveSensorId(row);

  return {
    ...row,
    ...primary,
    id,
    ID: row.ID ?? row.id ?? id,
    "overall score": overallScore,
    position: pos,
    coordinates: {
      local: { x: pos[0], y: pos[1], z: pos[2] },
      global: null,
    },
    windRoseSeries,
    primaryWindRoseKey: STANDARD_KEY,
  };
};

/** Normalize API rows (old or new) into a single shape for rendering. */
export const normalizeSensorPlacementRow = (row) => {
  if (!row) return row;

  if (!isNewSensorPlacementFormat(row)) {
    return wrapLegacySensorPlacementRow(row);
  }

  const local = row.position?.local ?? null;
  const global = row.position?.global ?? null;
  const windRoseSeries = extractWindRoseSeries(row);
  const primaryKey = windRoseSeries[STANDARD_KEY]
    ? STANDARD_KEY
    : Object.keys(windRoseSeries)[0] || STANDARD_KEY;
  const primary = windRoseSeries[primaryKey] || {};
  const overallScore = resolveOverallScore({
    ...row,
    windRoseSeries,
  });
  const id = resolveSensorId(row);

  return {
    ...row,
    ...primary,
    id,
    ID: row.ID ?? row.id ?? id,
    "overall score": overallScore,
    position: local
      ? toLocalArray(local)
      : Array.isArray(row.position)
        ? row.position
        : [0, 0, 0],
    coordinates: {
      local: local
        ? local
        : Array.isArray(row.position)
          ? { x: row.position[0], y: row.position[1], z: row.position[2] }
          : null,
      global,
    },
    windRoseSeries,
    primaryWindRoseKey: primaryKey,
  };
};

export const normalizeSensorPlacementResponse = (rows) => {
  if (!rows) return [];
  if (Array.isArray(rows)) return rows.map(normalizeSensorPlacementRow);
  if (typeof rows === "object") {
    return Object.entries(rows).map(([key, row]) =>
      normalizeSensorPlacementRow({
        ...row,
        ID: row?.ID ?? row?.id ?? key,
      }),
    );
  }
  return [];
};

export const buildDirectionalChartData = (directionalData = {}) => {
  const windDirectionList = [];
  const radarChartList = [];
  const uzFactorList = [];

  for (const [key, value] of Object.entries(directionalData ?? {})) {
    if (!isDirectionEntry(key, value)) continue;

    windDirectionList.push({
      direction: key,
      Simulated: parseFloat(key) + (value.direction?.[1] ?? 0),
      Ideal: key,
    });
    radarChartList.push({
      direction: key,
      velocity: value.velocity?.[1] ?? 0,
    });
    uzFactorList.push({
      direction: key,
      uzValue: 1 - parseFloat(value["Uz factor"] ?? 0),
    });
  }

  const byDirection = (a, b) => Number(a.direction) - Number(b.direction);
  windDirectionList.sort(byDirection);
  radarChartList.sort(byDirection);
  uzFactorList.sort(byDirection);

  return { windDirectionList, radarChartList, uzFactorList };
};

const WIND_ROSE_COLORS = [
  { stroke: "#0b5e04", fill: "#39b32e" },
  { stroke: "#b35c00", fill: "#e69500" },
  { stroke: "#003d99", fill: "#4d94ff" },
  { stroke: "#6b007b", fill: "#c44dd0" },
  { stroke: "#8b0000", fill: "#e64545" },
  { stroke: "#006666", fill: "#33cccc" },
];

export const getWindRoseSeriesColor = (index) =>
  WIND_ROSE_COLORS[index % WIND_ROSE_COLORS.length];

export const buildMultiSeriesWindRoseData = (seriesMap = {}) => {
  const ordered = orderWindRoseSeries(
    Object.fromEntries(
      Object.entries(seriesMap || {}).filter(([, value]) => value != null),
    ),
  );
  const byDirection = new Map();
  const seriesNames = Object.keys(ordered);

  seriesNames.forEach((seriesName) => {
    const { radarChartList } = buildDirectionalChartData(ordered[seriesName]);
    radarChartList.forEach(({ direction, velocity }) => {
      const key = String(direction);
      const existing = byDirection.get(key) || { direction };
      existing[seriesName] = velocity;
      byDirection.set(key, existing);
    });
  });

  return {
    data: Array.from(byDirection.values()).sort(
      (a, b) => Number(a.direction) - Number(b.direction),
    ),
    seriesNames,
  };
};

/** @deprecated use buildMultiSeriesWindRoseData */
export const buildCombinedWindRoseData = (standardData, overlapData) => {
  const seriesMap = {};
  if (standardData && Object.keys(standardData).length) {
    seriesMap[STANDARD_KEY] = standardData;
  }
  if (overlapData && Object.keys(overlapData).length) {
    seriesMap[OVERLAP_KEY] = overlapData;
  }
  return buildMultiSeriesWindRoseData(seriesMap).data;
};

export const hasCoordinateValue = (value) =>
  value !== null && value !== undefined && value !== "";

export const formatCoordinate = (value, digits = 3) => {
  if (!hasCoordinateValue(value)) return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toFixed(digits);
};

export const hasGlobalCoordinates = (global) =>
  !!global &&
  (hasCoordinateValue(global.latitude) ||
    hasCoordinateValue(global.longitude) ||
    hasCoordinateValue(global.height_above_sealevel_m));

export const hasLocalCoordinates = (local) =>
  !!local &&
  (hasCoordinateValue(local.x) ||
    hasCoordinateValue(local.y) ||
    hasCoordinateValue(local.z));
